import { useRouter } from 'fitify-ui-onboarding/src/hooks/useRouter'
import type { ImageProps } from 'fitify-ui-onboarding/src/types/ui'
import { useGrowthBook } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import { PAGES_CACHE } from 'fitify-ui-onboarding/src/utils/navigation'
import Head from 'next/head'
import { useTranslation } from 'next-i18next/pages'
import { useMemo } from 'react'

import { useLayoutTheme } from 'components/GenderFunnel/Layout/useLayoutTheme'
import { getRouteImages } from 'components/RoutePrefetch/RouteImagesPrefetch.Utils'
import { getPrefetchRoutes } from 'components/RoutePrefetch/RoutePrefetch.Utils'
import { useFunnel } from 'hooks/useFunnel'
import { useCategoryImageVariant } from 'hooks/useImageProps'
import { processImageDefinition } from 'utils/images'

type PreloadImage = ImageProps & {
  media?: string
}

export const RouteImagesPrefetch = () => {
  const funnel = useFunnel()
  const router = useRouter()
  const variant = useCategoryImageVariant(funnel)
  const growthBook = useGrowthBook()
  const { i18n } = useTranslation()
  const theme = useLayoutTheme()

  const images = useMemo<PreloadImage[]>(() => {
    if (!funnel) {
      return []
    }

    const imagesRoutes = getPrefetchRoutes(PAGES_CACHE, router.resolvedPathname)
    const locale = i18n.language === 'default' ? 'en' : i18n.language

    return imagesRoutes.reduce<PreloadImage[]>((acc, route) => {
      const imageDefinitions = getRouteImages(route, funnel, variant, {
        locale,
        theme: theme.type,
      })
      imageDefinitions.forEach((imageDefinition) => {
        const { prefetchCondition } = imageDefinition
        if (!prefetchCondition || prefetchCondition(growthBook)) {
          acc.push({
            ...processImageDefinition(imageDefinition),
            media: imageDefinition.media,
          })
        }
      })
      return acc
    }, [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    funnel,
    growthBook,
    i18n.language,
    router.isReady,
    router.resolvedPathname,
    theme.type,
    variant,
  ])

  return (
    <Head>
      {images.map((image) => (
        <link
          key={image.src}
          rel="preload"
          as="image"
          href={image.src}
          imageSrcSet={image.srcSet}
          media={image.media}
        />
      ))}
    </Head>
  )
}
